import { useAuthStore, type WargameAccount } from './authStore'

/**
 * Minimal GameCodex Accounts client for wargame sites — talks to the
 * same-origin proxy (`/api/accounts/*`, mounted via @gamecodex/world-bff),
 * which forwards to Atlas and keeps the refresh token in an httpOnly cookie.
 * The browser only ever holds the short-lived access token, in memory.
 *
 * Deliberately much smaller than world-ui's atlasAuthClient (no roles/tier,
 * no password login, no mock provider) — a wargame site only needs enough
 * identity to attach a Stripe Checkout entitlement to a real account.
 */
const BASE = '/api/accounts'

interface SessionResponse {
  accessToken?: string
  account?: WargameAccount
}

async function call(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  let res: Response
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    })
  } catch {
    return { ok: false, status: 0, data: { message: 'Could not reach the sign-in service.' } }
  }
  const text = await res.text()
  let data: Record<string, unknown> = {}
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>
    } catch {
      data = {}
    }
  }
  return { ok: res.ok, status: res.status, data }
}

function isSession(data: Record<string, unknown>): data is SessionResponse & Record<string, unknown> {
  return typeof data.accessToken === 'string' && !!data.account
}

function commit(data: SessionResponse): void {
  if (!data.account || !data.accessToken) return
  useAuthStore.getState().setSession(data.account, data.accessToken)
}

/** Ask Atlas to email a magic sign-in link. Enumeration-safe (always ok on valid input). */
export async function requestMagicLink(email: string): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!email) return { ok: false, error: 'A valid email is required.' }
  const r = await call('POST', '/request-magic-link', { email })
  if (r.status === 503) return { ok: false, error: 'Sign-in is not available right now.' }
  if (r.status === 400) return { ok: false, error: 'A valid email is required.' }
  return { ok: true }
}

/** Exchange an emailed magic-link token for a session. */
export async function verifyMagicLink(token: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const r = await call('GET', `/verify?token=${encodeURIComponent(token)}`)
  if (r.ok && isSession(r.data)) {
    commit(r.data)
    return { ok: true }
  }
  return { ok: false, error: (r.data.message as string) || 'This sign-in link is invalid or has expired.' }
}

/** Silently restore a session on app load via the httpOnly refresh cookie, if one exists. */
export async function bootstrapSession(): Promise<void> {
  const store = useAuthStore.getState()
  store.setRestoring(true)
  const r = await call('POST', '/refresh')
  if (r.ok && isSession(r.data)) commit(r.data)
  store.setRestoring(false)
}

export async function signOut(): Promise<void> {
  await call('POST', '/logout')
  useAuthStore.getState().clear()
}
